import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

import { PM_V2_Service } from '../../../Service/PM_V2/PM_V2.service';
import {
  PM_V2_Template_Interface,
  PM_V2_Template_Item_Interface,
  PM_V2_Template_Save_Interface,
  PM_V2_EmptyTemplateItem
} from '../../../Interface/PM_V2_Interface';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { NavService } from 'src/app/Service/Nav/Nav.service';

@Component({
  selector: 'app-pm-v2-template-add',
  templateUrl: './PM_V2_TemplateAdd.component.html',
  styleUrls: ['./PM_V2_TemplateAdd.component.css']
})
export class PM_V2_TemplateAddComponent implements OnInit {

  Procssing: boolean = false;

  TypeList: string[] = ['CHECK', 'PASS_FAIL', 'NUMBER', 'TEXT', 'DATE', 'SELECT'];

  Form: FormGroup = this.fb.group({
    id: [0],
    Previous_PM_V2_Template_ID: [null],
    PM_Code: ['', Validators.required],
    PM_Title: ['', Validators.required],
    Description: [''],
    Version: [1],
    Status: [true],
    Time_Estimated: [null],
    Default_Frequency: [null, Validators.min(1)],
    Default_Warning_Days: [null, Validators.min(0)],
    Date_Created: [null],
    Created_By: [null],
    items: this.fb.array([])
  });

  constructor(
    private fb: FormBuilder,
    private PM_V2_Service: PM_V2_Service,
    private Alertify_Service: AlertifyService,
    private router: Router,
    private location: Location,
    private Nav_Service: NavService,
  ) { }

  ngOnInit(): void {

    this.Nav_Service.CheckValidPage(2238).subscribe(
      data => {
            this.AddItem();
      }
      , error => {
        // AuthInterceptor owns authentication; feature errors must preserve credentials.
      }
    )
  }

  get Items(): FormArray {
    return this.Form.get('items') as FormArray;
  }

  NewItem(data?: PM_V2_Template_Item_Interface): FormGroup {
    const item = data || PM_V2_EmptyTemplateItem(this.Items ? this.Items.length + 1 : 1);

    if (!data) {
      item.Value_Frequency = this.OptionalNumber(this.Form.get('Default_Frequency')?.value);
      item.Days_Warning = this.OptionalNumber(this.Form.get('Default_Warning_Days')?.value);
    }

    return this.fb.group({
      id: [item.id || 0],
      PM_V2_Template_ID: [item.PM_V2_Template_ID],
      Order_Item: [item.Order_Item],
      Title_Item: [item.Title_Item || '', Validators.required],
      Type_Item: [item.Type_Item || 'CHECK', Validators.required],
      Required_Is: [item.Required_Is !== false],
      Criteria_Acceptance: [item.Criteria_Acceptance || ''],
      Instruction: [item.Instruction || ''],
      Value_Frequency: [item.Value_Frequency, [Validators.required, Validators.min(1)]],
      Days_Warning: [item.Days_Warning, [Validators.required, Validators.min(0)]],
      Active: [item.Active !== false]
    });
  }

  AddItem() {
    this.Items.push(this.NewItem());
    this.ReOrder();
  }

  MoveItemUp(index: number) {
    if (index <= 0) {
      return;
    }

    const control = this.Items.at(index);
    this.Items.removeAt(index);
    this.Items.insert(index - 1, control);
    this.ReOrder();
  }

  MoveItemDown(index: number) {
    if (index >= this.Items.length - 1) {
      return;
    }

    const control = this.Items.at(index);
    this.Items.removeAt(index);
    this.Items.insert(index + 1, control);
    this.ReOrder();
  }

  RemoveItem(index: number) {
    const group = this.Items.at(index) as FormGroup;
    const id = Number(group.get('id')?.value || 0);

    if (id > 0) {
      group.patchValue({ Active: false });
      this.ReOrder();
      return;
    }

    this.Items.removeAt(index);
    this.ReOrder();
  }

  ReOrder() {
    this.Items.controls.forEach((control, index) => {
      control.get('Order_Item')?.setValue(index + 1);
    });
  }

  private BuildRequest(): PM_V2_Template_Save_Interface {
    this.ReOrder();
    const value = this.Form.getRawValue();

    const model: PM_V2_Template_Interface = {
      id: Number(value.id || 0),
      Previous_PM_V2_Template_ID: value.Previous_PM_V2_Template_ID == null
        ? null
        : Number(value.Previous_PM_V2_Template_ID),
      PM_Code: value.PM_Code || '',
      PM_Title: value.PM_Title || '',
      Description: value.Description || '',
      Version: value.Version == null ? null : Number(value.Version),
      Status: value.Status === true,
      Time_Estimated: value.Time_Estimated === '' || value.Time_Estimated == null
        ? null
        : Number(value.Time_Estimated),
      Default_Frequency: this.OptionalNumber(value.Default_Frequency),
      Default_Warning_Days: this.OptionalNumber(value.Default_Warning_Days),
      Date_Created: value.Date_Created || null,
      Created_By: value.Created_By === '' || value.Created_By == null
        ? null
        : Number(value.Created_By)
    };

    const items: PM_V2_Template_Item_Interface[] = (value.items || []).map((x: any, index: number) => ({
      id: Number(x.id || 0),
      PM_V2_Template_ID: x.PM_V2_Template_ID == null ? null : Number(x.PM_V2_Template_ID),
      Order_Item: index + 1,
      Title_Item: x.Title_Item || '',
      Type_Item: x.Type_Item || 'CHECK',
      Required_Is: x.Required_Is === true,
      Criteria_Acceptance: x.Criteria_Acceptance || '',
      Instruction: x.Instruction || '',
      Value_Frequency: x.Value_Frequency == null || x.Value_Frequency === ''
        ? null
        : Number(x.Value_Frequency),
      Days_Warning: x.Days_Warning == null || x.Days_Warning === ''
        ? null
        : Number(x.Days_Warning),
      Active: x.Active !== false
    }));

    return {
      model,
      items
    };
  }

  private OptionalNumber(value: any): number | null {
    return value === '' || value == null ? null : Number(value);
  }

  private SyncPersisted(templateId: number) {
    this.PM_V2_Service.GetTemplateSingle(templateId).subscribe(data => {
      const model = data.model;
      this.Form.patchValue({
        id: model.id,
        Previous_PM_V2_Template_ID: model.Previous_PM_V2_Template_ID,
        PM_Code: model.PM_Code,
        PM_Title: model.PM_Title,
        Description: model.Description,
        Version: model.Version,
        Status: model.Status,
        Time_Estimated: model.Time_Estimated,
        Default_Frequency: model.Default_Frequency,
        Default_Warning_Days: model.Default_Warning_Days,
        Date_Created: model.Date_Created,
        Created_By: model.Created_By
      });

      while (this.Items.length) this.Items.removeAt(0);
      (data.items || [])
        .sort((a, b) => (a.Order_Item || 0) - (b.Order_Item || 0))
        .forEach(item => this.Items.push(this.NewItem(item)));

      this.Form.markAsPristine();
      this.location.replaceState('/pm-v2/template/edit/' + templateId);
      this.Procssing = false;
      this.Alertify_Service.SuccessMessage('اطلاعات با موفقیت ثبت شد.');
    }, error => this.HandleError(error));
  }

  private HandleError(error: any) {
    this.Procssing = false;

    this.Alertify_Service.ErrorMessage('خطا در ثبت اطلاعات.');
  }


  Save() {

    if (this.Form.invalid || this.Items.length === 0) {
      this.Form.markAllAsTouched();
      this.Alertify_Service.ErrorMessage('کد، عنوان، نسخه و اطلاعات ضروری آیتم‌ها را تکمیل کنید.');
      return;
    }

    this.Procssing = true;
    const request = this.BuildRequest();

    const templateId = Number(this.Form.get('id')?.value || 0);
    const saveRequest = templateId > 0
      ? this.PM_V2_Service.EditTemplate(request)
      : this.PM_V2_Service.AddTemplate(request);

    saveRequest.subscribe(
      data => {
        if (data && data.id === -2) {
          this.Procssing = false;
          this.Alertify_Service.ErrorMessage('این کد و نسخه قبلاً ثبت شده است.');
          return;
        }

        this.SyncPersisted(data.id);
      },
      error => this.HandleError(error)
    );
  }

  Cancel() {
    this.router.navigate(['/pm-v2/template']);
  }
}
